import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../components/Hero';
import WhyChooseUs from '../components/WhyChooseUs';
import CareerOutcomes from '../components/CareerOutcomes';
import Testimonials from '../components/Testimonials';
import { Shield, Brain, Terminal, Activity } from 'lucide-react';

const HomePage = ({ onRegister }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 🔹 HIGH-IMPACT SEO CONSTANTS
  const seoTitle = "Ciqura Labs | Best Cybersecurity, AI & IT Training in Indore";
  const seoDescription = "Ciqura Labs is Indore's premier IT training institute. Master Ethical Hacking, GRC, AIML, and Cloud at our Vijay Nagar lab. Training arm of Ciqur24 Bengaluru with 100% internship support.";
  const homeUrl = "https://ciquralabs.ciqur24.com/";

  // 🔹 MASTER 200-WORD TRENDING KEYWORD BLOCK FOR 2026 INDEXING
  const trendingKeywords = "Cybersecurity training in Indore, Ethical Hacking course Indore, Ciqura Labs, Best Cyber Security Institute in Indore, Vijay Nagar IT training, Cyber Security for beginners, Ethical hacking certification Indore, Infosec training Indore, Cybersecurity placement Indore, VAPT certification Indore, SOC Analyst course Indore, Cyber Security classes in Vijay Nagar, Network Security training Indore, Information security courses MP, Cyber security internship 2026, Hands-on hacking labs, Cyber career roadmap, AI training Indore, Machine Learning course Indore, AIML training Vijay Nagar, Generative AI course, Prompt Engineering training, AI for Cybersecurity training, Deep Learning classes Indore, Python for AI/ML, AI Security Engineer course, MLOps training, Data Science and AI Indore, Artificial Intelligence certification, Best AI institute Indore, AI-powered threat detection training, GRC training online, Cloud security governance, Microsoft 365 Admin course, Cisco Networking Indore, Full Stack Web Development Indore, Web dev classes Indore, Python programming Indore, Cloud SOC Analyst, API Security Pentesting, DevSecOps certification, Red Teaming training, Zero Trust Architecture course, Bug Bounty hunting, Post-Quantum Cryptography, AI Forensics, Cybersecurity Bengaluru internship, IT training near me, Ethical hacking classes Vijay Nagar, Cyber Security career Indore, Hacking certification online, Industrial Cybersecurity training, Office 113 Shagun Arcade Indore training, Ciqura Labs Indore courses, IT coaching Indore, Indore Cyber Cell training partner, Career in Cyber Security India, Top tech courses 2026, Ciqur24 Training Division";

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={trendingKeywords} />
        <link rel="canonical" href={homeUrl} />

        {/* Open Graph / Social Media */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content="https://res.cloudinary.com/dlz8euavh/image/upload/v1769179291/3_zbjtyy.png" />
        <meta property="og:url" content={homeUrl} />
        <meta property="og:type" content="website" />

        {/* Twitter Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />

        {/* 🔹 MASTER SCHEMA INTEGRATION */}
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "@id": homeUrl,
              "name": "Ciqura Labs",
              "image": "https://res.cloudinary.com/dlz8euavh/image/upload/v1769179291/3_zbjtyy.png",
              "telephone": "+91-9303998387",
              "url": homeUrl,
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "113, First Floor, Shagun Arcade, Vijay Nagar",
                "addressLocality": "Indore",
                "addressRegion": "MP",
                "postalCode": "452010",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 22.7533,
                "longitude": 75.8937
              },
              "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                "opens": "10:00",
                "closes": "19:00"
              },
              "priceRange": "$$"
            },
            {
              "@context": "https://schema.org",
              "@type": "EducationalOrganization",
              "name": "Ciqura Labs",
              "parentOrganization": {
                "@type": "Organization",
                "name": "Ciqur24 (CYBWK Pvt Ltd)",
                "location": "Bengaluru, India"
              },
              "description": "Premier training division specializing in Cybersecurity, AI, and Cloud technologies.",
              "logo": "https://res.cloudinary.com/dlz8euavh/image/upload/v1769179291/3_zbjtyy.png"
            }
          ])}
        </script>
      </Helmet>

      {/* Main Page Layout */}
      <main style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
        
        {/* 🚀 Active Hero Section (Maintains all functional registration animation handlers) */}
        <Hero onRegister={onRegister} />
        
        {/* 👁️ HIGH-IMPACT VISIBLE HTML CONTENT SECTION FOR TOP-TIER RANKING */}
        <section style={{ padding: '80px 7.6923%', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            
            <header style={{ marginBottom: '50px', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(0, 255, 209, 0.1)', color: 'var(--brand-primary)', padding: '6px 16px', borderRadius: '4px', fontSize: '13px', fontWeight: '700', marginBottom: '16px', border: '1px solid rgba(0, 255, 209, 0.2)' }}>
                <Activity size={14} /> CENTRAL INDIA'S ELITE INDUSTRIAL ACADEMY
              </div>
              <h2 className="display-medium" style={{ marginBottom: '16px', color: '#fff' }}>
                Advanced Tech & Security Training Hub in Vijay Nagar, Indore
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', margin: '0 auto' }}>
                Ciqura Labs bridges the gap between traditional engineering studies and real enterprise infrastructure operations. Discover our foundational vectors engineered for modern career roles.
              </p>
            </header>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px', marginTop: '40px' }}>
              
              {/* Core Vertical 1: Cybersecurity & Hacking */}
              <article style={seoCardStyle}>
                <div style={iconBoxStyle}><Shield size={26} /></div>
                <h3 className="heading-2" style={{ color: '#fff', marginBottom: '12px' }}> Cyber Security & Ethical Hacking</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  Our primary flagship domain focuses completely on Defensive Operations, Threat Hunting, and active Vulnerability Assessment and Penetration Testing (VAPT). Train inside a highly virtualized sandbox environment and graduate ready to step directly into enterprise Security Operations Centers (SOC).
                </p>
              </article>

              {/* Core Vertical 2: AIML & Python */}
              <article style={seoCardStyle}>
                <div style={iconBoxStyle}><Brain size={26} /></div>
                <h3 className="heading-2" style={{ color: '#fff', marginBottom: '12px' }}>AIML Architecture & Python Engineering</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  Master foundational **Python Programming** and progress cleanly into complex predictive algorithm models, deep data structures, Neural Networks, and Generative AI prompt optimization. Learn how to write code meant for real machine learning pipelines and production environments.
                </p>
              </article>

              {/* Core Vertical 3: IT Support & Core Infrastructure */}
              <article style={seoCardStyle}>
                <div style={iconBoxStyle}><Terminal size={26} /></div>
                <h3 className="heading-2" style={{ color: '#fff', marginBottom: '12px' }}>Enterprise IT Support & Network Ops</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  The structural base of every global technology ecosystem. Our IT Support Professionals path teaches complete physical hardware systems diagnostic tracking, Cisco network architecture controls, routing topologies, cloud identity systems, and production Microsoft 365 tenant management.
                </p>
              </article>

            </div>
          </div>
        </section>

        {/* Supporting Layout Core Content Blocks */}
        <WhyChooseUs />
        <CareerOutcomes />
        <Testimonials />
      </main>
    </>
  );
};

// Inline Layout Styling Objects
const seoCardStyle = {
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-subtle)',
  padding: '36px',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start'
};

const iconBoxStyle = {
  color: 'var(--brand-primary)',
  background: 'rgba(0, 255, 209, 0.05)',
  padding: '10px',
  borderRadius: '6px',
  marginBottom: '20px'
};

export default HomePage;